import * as React from 'react';

import { useLiveRef } from './useLiveRef';

type Fn<ARGS extends any[], R> = (...args: ARGS) => R;

/**
 * React hook `useEventCallback`
 * Aimed to be easier to use than `useCallback` and solve problems raised in [this ticket](https://github.com/facebook/react/issues/14099).
 *
 * `useEventCallback` doesn't need any dependencies list.
 * The returned function should not be used during rendering.
 *
 * @example
 * const Input = () => {
 *   const [value, setValue] = useState('');
 *   const onChange = useEventCallback((event) => {
 *     setValue(event.target.value);
 *   });
 *   return <input value={value} onChange={onChange} />;
 * }
 */
export const useEventCallback = <A extends any[], R>(fn: Fn<A, R>): Fn<A, R> => {
    const ref = useLiveRef<Fn<A, R>>(fn);
    return React.useMemo(
        () =>
            (...args: A): R =>
                ref.current(...args),
        [ref]
    );
};
